/**
 * The first screen's figures and its two activity feeds.
 *
 * One call rather than eight: the dashboard is a header, and a header that
 * fires eight requests takes eight times as long to stop looking broken. The
 * backoffice API answers it that way too — `dashboard.overview` sends the
 * summary, the account split, the renewal rate and both feeds in one body, so
 * this module is a single request and the mapping that follows it.
 *
 * ## Read defensively all the same
 *
 * Every field is taken with a fallback rather than assumed: a missing count is
 * 0, a missing feed is no rows, an unfamiliar plan or status falls back to one
 * the panel can draw. A table showing its empty state is a screen somebody can
 * still use; a `TypeError` half way through mapping is not, and the difference
 * costs one `??` per field.
 *
 * Money arrives as a decimal string — `"3632.00"` — because the server keeps it
 * exact and JSON has no decimal type. It is parsed to a number here and written
 * by `formatRupees` at the edge, so nothing in between has to know either.
 *
 * Dates go the other way: `formatDate` is the users feature's, shared rather
 * than copied, because both feeds print the same two shapes the same API sends
 * the roster — a bare day and an ISO instant.
 */

import { API_ENDPOINTS, api } from "../../../lib/api";
import type {
  AccountStatusSlice,
  DashboardData,
  RenewalRate,
} from "../types/dashboard.types";
// The feed's status is a payment's, and the revenue module owns that
// vocabulary — one list of five words read two ways would be two lists.
// Straight at the service rather than through the revenue barrel, which
// re-exports that feature's page: this needs one mapper, not a screen.
import { toPaymentStatus } from "../../revenue/services/revenueService";
import { formatDate, type UserPlan } from "../../users";

/**
 * How many rows each of the dashboard's feeds shows.
 *
 * Five, and five is the whole of it — this screen is a glance, not a list. The
 * cap is applied here rather than in the tables so a backend that answers with
 * fifty cannot quietly turn the first screen into two more roster pages; the
 * Users screen is where a list gets paged through.
 */
export const RECENT_ACTIVITY_COUNT = 5;

/* -------------------------------------------------------------------------
   The wire shape
   ---------------------------------------------------------------------- */

interface DashboardResponse {
  summary?: SummaryResponse | null;
  recent_subscriptions?: SubscriptionResponse[] | null;
  recent_signups?: SignupResponse[] | null;
}

interface SummaryResponse {
  new_accounts_this_month?: number | null;
  /** Decimal strings — see the note at the top of the file. */
  annual_revenue?: string | number | null;
  monthly_revenue?: string | number | null;
  total_accounts?: number | null;
  trial_accounts?: number | null;
  pro_accounts?: number | null;
  account_status_breakdown?: StatusSliceResponse[] | null;
  renewal_rate?: RenewalRateResponse | null;
}

interface StatusSliceResponse {
  label?: string | null;
  count?: number | null;
  percentage?: number | null;
}

interface RenewalRateResponse {
  current_month_pct?: number | null;
  previous_month_pct?: number | null;
  delta_pct?: number | null;
}

interface SubscriptionResponse {
  /** Which account was billed. The feed carries no id of its own. */
  workspace_id: number;
  account_name?: string | null;
  plan?: string | null;
  amount?: string | number | null;
  /**
   * Always "INR" so far, and deliberately not read: the panel writes rupees,
   * and the day a second currency turns up it needs a column of its own rather
   * than a symbol that quietly changes under the same figures.
   */
  currency?: string | null;
  /** ISO 8601, with an offset. */
  date?: string | null;
  status?: string | null;
  /** How many clients that account has. Not shown here — the roster's column. */
  clients?: number | null;
}

interface SignupResponse {
  workspace_id: number;
  account_name?: string | null;
  /** Already one line — "Pune, MH" — rather than city and country apart. */
  location?: string | null;
  phone?: string | null;
  email?: string | null;
  /** A day, `YYYY-MM-DD`, not an instant. */
  date?: string | null;
}

/* -------------------------------------------------------------------------
   One field at a time
   ---------------------------------------------------------------------- */

/** A count, or 0 for anything that is not one. */
const toCount = (value: number | null | undefined): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

/** Money, out of the decimal string the server sends it as. */
const toAmount = (value: string | number | null | undefined): number => {
  const amount = typeof value === "string" ? Number(value) : value;
  return typeof amount === "number" && Number.isFinite(amount) ? amount : 0;
};

/** A percentage that is allowed to be absent — see `RenewalRate`. */
const toPercent = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** An account with no name on it yet is still a row somebody has to click. */
const toAccountName = (value: string | null | undefined): string =>
  value?.trim() || "Unnamed account";

/** An empty string is this API's way of saying it has nothing, the same as a
 *  null — both print as the dash the tables use for a blank cell. */
const toText = (value: string | null | undefined): string =>
  value?.trim() || "—";

/** A plan the panel has not been taught about falls back rather than throwing —
 *  one unfamiliar word should not blank the whole feed. */
const toPlan = (value: string | null | undefined): UserPlan =>
  value === "Monthly" || value === "Yearly" ? value : "Free trial";

const toStatusBreakdown = (
  rows: StatusSliceResponse[] | null | undefined
): AccountStatusSlice[] =>
  (rows ?? [])
    // A slice with no name is a slice nothing can label, and an unlabelled
    // wedge is worse than one wedge fewer.
    .filter((row) => Boolean(row.label?.trim()))
    .map((row) => ({
      label: (row.label ?? "").trim(),
      count: toCount(row.count),
      percentage: toCount(row.percentage),
    }));

const toRenewalRate = (
  rate: RenewalRateResponse | null | undefined
): RenewalRate => ({
  currentMonthPct: toPercent(rate?.current_month_pct),
  previousMonthPct: toPercent(rate?.previous_month_pct),
  deltaPct: toPercent(rate?.delta_pct),
});

/* -------------------------------------------------------------------------
   The call
   ---------------------------------------------------------------------- */

/**
 * Everything the dashboard draws.
 *
 * Nothing identifying the admin goes with it: the route answers for whoever
 * the access token belongs to, and the API client attaches that already.
 */
export const getDashboard = async (): Promise<DashboardData> => {
  const data = await api.get<DashboardResponse>(
    API_ENDPOINTS.dashboard.overview
  );

  const summary = data.summary ?? {};

  return {
    summary: {
      totalAccounts: toCount(summary.total_accounts),
      activeTrial: toCount(summary.trial_accounts),
      proAccounts: toCount(summary.pro_accounts),
      newThisMonth: toCount(summary.new_accounts_this_month),
      annualRevenue: toAmount(summary.annual_revenue),
      monthlyRevenue: toAmount(summary.monthly_revenue),
      statusBreakdown: toStatusBreakdown(summary.account_status_breakdown),
      renewalRate: toRenewalRate(summary.renewal_rate),
    },
    recentSubscriptions: (data.recent_subscriptions ?? [])
      .slice(0, RECENT_ACTIVITY_COUNT)
      .map((row) => ({
        // The account and the moment together — see the note on
        // `SubscriptionActivity.id` for why neither is enough on its own.
        id: `${row.workspace_id}-${row.date ?? ""}`,
        userId: row.workspace_id,
        accountName: toAccountName(row.account_name),
        plan: toPlan(row.plan),
        amount: toAmount(row.amount),
        date: formatDate(row.date),
        status: toPaymentStatus(row.status),
      })),
    recentSignups: (data.recent_signups ?? [])
      .slice(0, RECENT_ACTIVITY_COUNT)
      .map((row) => ({
        userId: row.workspace_id,
        accountName: toAccountName(row.account_name),
        location: toText(row.location),
        phone: toText(row.phone),
        email: toText(row.email),
        date: formatDate(row.date),
      })),
  };
};
